import { asNumberOrNull, asStringOrNull } from '@/shared/utils/type-utils';

import { CADASTRAL_POSITION_PROCESS_TYPE } from './integration-monitoring-config';
import {
  IntegrationMonitoringFilter,
  IntegrationMonitoringIndicators,
  IntegrationMonitoringModel,
  IntegrationMonitoringPagination,
  IntegrationMonitoringRequestItem,
} from './integration-monitoring-types';

export const defaultFilter = (): IntegrationMonitoringFilter => ({
  integrationType: 'API',
  processType: 'COPARTICIPATION',
  carrierId: null,
  financialGroupId: null,
  contractId: null,
  periodStart: null,
  periodEnd: null,
  status: [],
});

export const defaultPagination = (): IntegrationMonitoringPagination => ({
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
});

export const initState: IntegrationMonitoringModel = {
  filter: defaultFilter(),
  pagination: defaultPagination(),
  loadingPage: false,
  loadingTable: false,
  loadingExport: false,
  exportingById: {},
  records: [],
  indicators: null,
  showSnackbarCustomize: { show: false, type: null, message: '' },
};

const STATUS_ALIASES: Record<string, string> = {
  PROCESSED: 'COMPLETED',
  PARTIALLY_PROCESSED: 'COMPLETED_WITH_ERRORS',
  ERROR: 'COMPLETED_WITH_ERRORS',
};

export const normalizeStatus = (
  status: string | null,
  processType?: string,
): string | null => {
  if (!status) return null;
  if (processType === CADASTRAL_POSITION_PROCESS_TYPE) {
    return status;
  }
  return STATUS_ALIASES[status] ?? status;
};

export const buildFilterParams = (filter: IntegrationMonitoringFilter): Record<string, unknown> => {
  const params: Record<string, unknown> = {};

  if (filter.processType === CADASTRAL_POSITION_PROCESS_TYPE) {
    if (filter.integrationType) params.integrationType = filter.integrationType;
    params.processType = filter.processType;
  }

  if (filter.carrierId !== null && filter.carrierId !== undefined) params.carrierId = filter.carrierId;
  if (filter.financialGroupId !== null && filter.financialGroupId !== undefined) {
    params.financialGroupId = filter.financialGroupId;
  }
  if (filter.contractId !== null && filter.contractId !== undefined) params.contractId = filter.contractId;
  if (filter.periodStart) params.receivedStart = filter.periodStart;
  if (filter.periodEnd) params.receivedEnd = filter.periodEnd;
  if (filter.status.length) params.status = filter.status[0];

  return params;
};

const firstValue = (source: unknown, keys: string[]): unknown => {
  if (!source || typeof source !== 'object') return null;

  return keys.reduce((found: unknown, key) => {
    if (found !== null && found !== undefined && found !== '') return found;
    const value = key.split('.').reduce((current: unknown, path) => {
      if (current === null || current === undefined || typeof current !== 'object') return null;
      return (current as Record<string, unknown>)[path];
    }, source);

    return value !== null && value !== undefined && value !== '' ? value : null;
  }, null);
};

const namedEntityToString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return asStringOrNull(record.name ?? record.description ?? record.legalName ?? record.id);
  }
  return null;
};

const contentFromPayload = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object' || payload === null) return [];

  const data = payload as Record<string, unknown>;
  if (Array.isArray(data.content)) return data.content;
  if (Array.isArray(data.data)) return data.data;

  const nestedData = data.data;
  if (typeof nestedData === 'object' && nestedData !== null && Array.isArray((nestedData as Record<string, unknown>).content)) {
    return (nestedData as Record<string, unknown>).content as unknown[];
  }

  return [];
};

export const normalizePage = (payload: unknown) => {
  const pagePayload = typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as Record<string, unknown>).data ?? payload
    : payload ?? {};
  const pageData = pagePayload as Record<string, unknown>;
  const content = contentFromPayload(pagePayload);
  const totalElements = Number(
    pageData.totalElements
      ?? pageData.total
      ?? (typeof pageData.data === 'object' && pageData.data !== null
        ? (pageData.data as Record<string, unknown>).totalElements
        : undefined)
      ?? content.length,
  );

  return {
    content,
    totalElements: Number.isNaN(totalElements) ? content.length : totalElements,
  };
};

export const normalizeRequest = (
  row: unknown,
  processType?: string,
): IntegrationMonitoringRequestItem => {
  const idKeys = processType === CADASTRAL_POSITION_PROCESS_TYPE
    ? ['requestId', 'id', 'batchId']
    : ['batchId', 'requestId', 'id'];
  const batchId = String(firstValue(row, idKeys) ?? '');
  const errorItems = asNumberOrNull(firstValue(row, ['errorItems', 'errorCount']));
  const rawStatus = asStringOrNull(firstValue(row, ['status']));
  const status = normalizeStatus(rawStatus, processType);
  const explicitMessage = asStringOrNull(firstValue(row, [
    'errorMessage',
    'failureReason',
    'standardizedMessage',
    'validationMessage',
  ]));

  return {
    batchId,
    carrier: namedEntityToString(firstValue(row, ['carrier', 'carrierName'])),
    financialGroup: namedEntityToString(firstValue(row, ['financialGroup', 'financialGroupName'])),
    contract: namedEntityToString(firstValue(row, ['contract', 'contractNumber', 'policyNumber', 'contract.name'])),
    competence: asStringOrNull(firstValue(row, ['competence', 'competency'])),
    status,
    receivedAt: asStringOrNull(firstValue(row, ['receivedAt', 'receivedDateTime'])),
    totalItems: asNumberOrNull(firstValue(row, ['totalItems', 'itemCount', 'recordsReturned'])),
    errorItems,
    errorMessage: explicitMessage,
  };
};

export const normalizeIndicators = (
  payload: unknown,
  filterFallback?: Pick<IntegrationMonitoringFilter, 'periodStart' | 'periodEnd'>,
): IntegrationMonitoringIndicators => {
  const source = typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as Record<string, unknown>).data ?? payload
    : payload ?? {};
  const data = source as Record<string, unknown>;
  const periodSource = (data.period ?? {}) as Record<string, unknown>;

  return {
    totalRequests: Number(data.totalRequests ?? 0),
    totalWithErrors: Number(data.totalWithErrors ?? 0),
    period: {
      start: asStringOrNull(periodSource.start) ?? filterFallback?.periodStart ?? '',
      end: asStringOrNull(periodSource.end) ?? filterFallback?.periodEnd ?? '',
    },
  };
};

export const normalizeMonitoringPage = (payload: unknown, processType?: string) => {
  const page = normalizePage(payload);
  return {
    content: page.content.map((row) => normalizeRequest(row, processType)),
    totalElements: page.totalElements,
  };
};

/** @deprecated Use normalizeMonitoringPage */
export const normalizeBatchesPage = normalizeMonitoringPage;
